const freeze = (value) => Object.freeze(value);

export const THEME_A_TOKENS = freeze({
  fonts: freeze({
    display: '"Akongo V2 Foco", "Inter", ui-sans-serif, system-ui, sans-serif',
    body: '"Inter", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  }),
  colors: freeze({
    text: "#173C32",
    textMuted: "#4F635A",
    surface: "#FFFFFF",
    surfaceSubtle: "#F5F8F6",
    border: "#DCE6E0",
    borderStrong: "#BFCFC6",
    brand: "#017A42",
    brandHover: "#006B39",
    brandSoft: "#E8F5EE",
    brandInk: "#FFFFFF",
    highlight: "#F3C94E",
    highlightHover: "#E4B92E",
    highlightSoft: "#FFF8DC",
    highlightInk: "#173C32",
    critical: "#A93630",
    criticalSoft: "#FFF0EF",
    focus: "#0A6EBC",
    collectionObservation: "#16825F",
    collectionQuestionnaire: "#2F73B7",
    collectionScan: "#82519A",
    // Évaluations rapides (grilles) : ambre sombre, famille de l'ambre data-viz que le
    // tableau de bord donne déjà aux évaluations rapides ; 5,6:1 sur blanc.
    collectionFastEvaluation: "#9A5800",
    collectionObservationSoft: "#E8F5EE",
    collectionQuestionnaireSoft: "#EAF1F9",
    collectionScanSoft: "#F2ECF6",
    collectionFastEvaluationSoft: "#FBF1E3",
    // Domaines (palette unique, 26/10/2026) : un domaine = une couleur + une icône partout
    // (chronologie, tableau de bord, fiche). Les quatre types de collecte ci-dessus sont des
    // domaines ; ces sept-là complètent la palette, choisis pour s'écarter des quatre et entre
    // eux (CIEDE2000 ≥ 14) et pour porter une icône blanche à ≥ 3:1. Invariants entre thèmes,
    // comme les collectes. L'alimentation, trop claire pour le blanc (2,4:1), porte une encre foncée.
    domainHealth: "#D14343",
    domainEnrichment: "#6E9E1A",
    domainFeeding: "#E39B00",
    domainFeedingInk: "#3D2E00",
    domainTraining: "#3D4FD1",
    domainEnvironment: "#0C93B4",
    domainMovement: "#7C6A5C",
    domainNote: "#D9468A",
    // Teinte du haut de page (dégradé vers surfaceSubtle) — piste « Relief & lumière ».
    surfaceCanvasTint: "#E9F2ED",
    // Surfaces « inverses » (barre latérale, pilules sombres du header) : un
    // fond sombre stable dans TOUS les thèmes — jamais dérivé de text, qui
    // s'inverse en thème sombre.
    surfaceInverse: "#173C32",
    // Bandeau de marque en haut du rail : un cran au-dessus de la surface
    // inverse. Thémable — sans quoi il garderait le même vert sur un rail
    // sombre neutre (nuit) ou noir (contraste).
    surfaceInverseRaised: "#294A3E",
    surfaceInverseInk: "#FFFFFF",
    // Encres et surfaces du rail, alpha compris : l'échelle n'est pas
    // transposable d'un fond sombre à un fond clair (la courbe de luminance
    // n'est pas symétrique), donc chaque thème porte la sienne.
    inverseInkIdle: "rgba(255, 255, 255, 0.74)",
    inverseInkMuted: "rgba(255, 255, 255, 0.64)",
    inverseInkSubtle: "rgba(255, 255, 255, 0.56)",
    inverseInkDisabled: "rgba(255, 255, 255, 0.52)",
    inverseSurfaceHover: "rgba(255, 255, 255, 0.09)",
    inverseSurfaceActive: "rgba(255, 255, 255, 0.15)",
    inverseRule: "rgba(255, 255, 255, 0.14)",
    // Repère d'état actif du rail : le jaune ne tient pas sur un rail clair
    // (1,3:1), les thèmes clairs le remplacent par leur primaire.
    surfaceInverseAccent: "#F3C94E",
    // Focus sur surface inverse : le bleu de focus standard ne vaut que
    // 2,3:1 sur un rail sombre, sous le seuil 3:1 de WCAG 1.4.11.
    focusInverse: "#5FA8DC",
    // Reflet du shimmer des squelettes (blanc doux en clair, discret en sombre).
    skeletonShimmer: "rgba(255, 255, 255, 0.68)",
    // Palette d'ambiance : éléments SANS sémantique (avatars, tags libres,
    // séries de graphiques). Attribution stable par entité via getAmbianceColor.
    // Jamais pour un statut ni un type de collecte.
    ambianceTerracotta: "#C1694F",
    ambianceSable: "#B98A45",
    ambianceOlive: "#8A9A5B",
    ambianceSauge: "#7FA08C",
    ambianceArdoise: "#5E7E9B",
    ambiancePrune: "#96687F",
    // Palette data-viz : séries de graphiques SANS sémantique de collecte
    // (notes par type, enrichissements…). Plus vive que l'ambiance (réservée
    // aux avatars/tags) ; thémable — le thème daltonien la remplace par la
    // palette Okabe-Ito.
    datavizCorail: "#EE6352",
    datavizAmbre: "#F2A93B",
    datavizTurquoise: "#14B8A6",
    datavizBleu: "#3E7DE8",
    datavizViolet: "#8A5CF5",
    datavizRose: "#E0559C",
    // Signaux data-viz (segments de barres, jauges) : statuts qui ne sont ni
    // des collectes ni des intents de badge. Lisibilité daltonisme : écarts de
    // clarté marqués entre segments d'une même barre.
    signalInformation: "#2F73B7",
    signalProgress: "#56B4E9",
    signalWarning: "#D97706",
    // Encre d'une pastille sur le signal d'alerte (priorité P2) : 5,7:1, le blanc n'en tient que 3,2.
    signalWarningInk: "#1F1300",
    // Résultats d'évaluation (positif / neutre / négatif) : vert, orange, rouge
    // dans tous les thèmes, indépendants de la marque (qui est bleue en ardoise
    // ou daltonien). Seul le thème daltonien les remplace (bleu / orange / vermillon).
    // L'encre `-ink` tient 4,5:1 sur la couleur pleine (badges).
    outcomePositive: "#1E7A3C",
    outcomePositiveInk: "#FFFFFF",
    outcomeNeutral: "#B85C00",
    outcomeNeutralInk: "#FFFFFF",
    outcomeNegative: "#B42318",
    outcomeNegativeInk: "#FFFFFF",
    // Catégories de questions — constantes V1 imposées (ex-src/tools/style/colors.js).
    categoryQuestion1: "#23b7e5",
    categoryQuestion2: "#ff8f29",
    categoryQuestion3: "#f0504f",
    categoryQuestion4: "#26c24b",
    categoryQuestionDefault: "#ad43db",
    categoryQuestion1Fade: "#8ed3e8",
    categoryQuestion2Fade: "#fccba1",
    categoryQuestion3Fade: "#efa2a2",
    categoryQuestion4Fade: "#88e09d",
    categoryQuestionDefaultFade: "#c688e0",
    // Catégories d'éthogramme — palette V1 imposée (obsAnalytics._getCatColor).
    categoryEthogram1: "#06D6A0",
    categoryEthogram2: "#118AB2",
    categoryEthogram3: "#F5AB00",
    categoryEthogram4: "#EF476F",
    categoryEthogram5: "#47366D",
    categoryEthogram6: "#7A7265",
    categoryEthogram0: "#000000",
    categoryEthogramFallback: "#073B4C",
  }),
  geometry: freeze({
    // En rem (40 px à 100 %) : la hauteur des contrôles suit l'échelle de
    // l'interface (90 % en V2, agrandissements d'accessibilité).
    controlHeight: "2.5rem",
    radiusControl: "8px",
    radiusSurface: "12px",
    shadowSurface: "0 2px 4px rgba(23, 60, 50, 0.08), 0 12px 32px rgba(23, 60, 50, 0.18)",
    shadowOverlay: "0 12px 28px rgba(23, 60, 50, 0.16), 0 2px 6px rgba(23, 60, 50, 0.1)",
    shadowRaised: "0 2px 4px rgba(23, 60, 50, 0.05), 0 10px 24px rgba(23, 60, 50, 0.09)",
    shadowBrandHalo: "0 4px 10px rgba(1, 122, 66, 0.28)",
    // Bord du rail. Sur un thème clair le rail ne se détache presque pas de
    // la page (1,1:1) : c'est cette ombre qui en pose la limite, d'où sa
    // densité doublée là-bas.
    shadowRail: "6px 0 18px rgba(23, 60, 50, 0.06)",
  }),
  // 4 / 8 / 12 / 16 / 24 / 32 px à 100 %, en rem pour suivre l'échelle.
  spacing: freeze({
    1: "0.25rem",
    2: "0.5rem",
    3: "0.75rem",
    4: "1rem",
    5: "1.5rem",
    6: "2rem",
  }),
  motion: freeze({
    durationFast: "120ms",
    durationStandard: "180ms",
    durationEntrance: "260ms",
    durationAccent: "400ms",
    easingStandard: "cubic-bezier(0.2, 0, 0, 1)",
    // Ressort : réservé aux confirmations (pop de succès). Un seul easing rebondi.
    easingSpring: "cubic-bezier(0.34, 1.56, 0.64, 1)",
  }),
});

export const THEME_A_CSS_VARIABLES = freeze({
  "--ak-v2-font-display": THEME_A_TOKENS.fonts.display,
  "--ak-v2-font-body": THEME_A_TOKENS.fonts.body,
  "--ak-v2-text": THEME_A_TOKENS.colors.text,
  "--ak-v2-text-muted": THEME_A_TOKENS.colors.textMuted,
  "--ak-v2-surface": THEME_A_TOKENS.colors.surface,
  "--ak-v2-surface-subtle": THEME_A_TOKENS.colors.surfaceSubtle,
  "--ak-v2-border": THEME_A_TOKENS.colors.border,
  "--ak-v2-border-strong": THEME_A_TOKENS.colors.borderStrong,
  "--ak-v2-brand": THEME_A_TOKENS.colors.brand,
  "--ak-v2-brand-hover": THEME_A_TOKENS.colors.brandHover,
  "--ak-v2-brand-soft": THEME_A_TOKENS.colors.brandSoft,
  "--ak-v2-brand-ink": THEME_A_TOKENS.colors.brandInk,
  "--ak-v2-highlight": THEME_A_TOKENS.colors.highlight,
  "--ak-v2-highlight-hover": THEME_A_TOKENS.colors.highlightHover,
  "--ak-v2-highlight-soft": THEME_A_TOKENS.colors.highlightSoft,
  "--ak-v2-highlight-ink": THEME_A_TOKENS.colors.highlightInk,
  "--ak-v2-critical": THEME_A_TOKENS.colors.critical,
  "--ak-v2-critical-soft": THEME_A_TOKENS.colors.criticalSoft,
  "--ak-v2-focus": THEME_A_TOKENS.colors.focus,
  "--ak-v2-collection-observation": THEME_A_TOKENS.colors.collectionObservation,
  "--ak-v2-collection-questionnaire": THEME_A_TOKENS.colors.collectionQuestionnaire,
  "--ak-v2-collection-scan": THEME_A_TOKENS.colors.collectionScan,
  "--ak-v2-collection-fast-evaluation": THEME_A_TOKENS.colors.collectionFastEvaluation,
  "--ak-v2-collection-observation-soft": THEME_A_TOKENS.colors.collectionObservationSoft,
  "--ak-v2-collection-questionnaire-soft": THEME_A_TOKENS.colors.collectionQuestionnaireSoft,
  "--ak-v2-collection-scan-soft": THEME_A_TOKENS.colors.collectionScanSoft,
  "--ak-v2-collection-fast-evaluation-soft": THEME_A_TOKENS.colors.collectionFastEvaluationSoft,
  "--ak-v2-domain-health": THEME_A_TOKENS.colors.domainHealth,
  "--ak-v2-domain-enrichment": THEME_A_TOKENS.colors.domainEnrichment,
  "--ak-v2-domain-feeding": THEME_A_TOKENS.colors.domainFeeding,
  "--ak-v2-domain-feeding-ink": THEME_A_TOKENS.colors.domainFeedingInk,
  "--ak-v2-domain-training": THEME_A_TOKENS.colors.domainTraining,
  "--ak-v2-domain-environment": THEME_A_TOKENS.colors.domainEnvironment,
  "--ak-v2-domain-movement": THEME_A_TOKENS.colors.domainMovement,
  "--ak-v2-domain-note": THEME_A_TOKENS.colors.domainNote,
  "--ak-v2-surface-canvas-tint": THEME_A_TOKENS.colors.surfaceCanvasTint,
  "--ak-v2-surface-inverse": THEME_A_TOKENS.colors.surfaceInverse,
  "--ak-v2-surface-inverse-raised": THEME_A_TOKENS.colors.surfaceInverseRaised,
  "--ak-v2-surface-inverse-ink": THEME_A_TOKENS.colors.surfaceInverseInk,
  "--ak-v2-inverse-ink-idle": THEME_A_TOKENS.colors.inverseInkIdle,
  "--ak-v2-inverse-ink-muted": THEME_A_TOKENS.colors.inverseInkMuted,
  "--ak-v2-inverse-ink-subtle": THEME_A_TOKENS.colors.inverseInkSubtle,
  "--ak-v2-inverse-ink-disabled": THEME_A_TOKENS.colors.inverseInkDisabled,
  "--ak-v2-inverse-surface-hover": THEME_A_TOKENS.colors.inverseSurfaceHover,
  "--ak-v2-inverse-surface-active": THEME_A_TOKENS.colors.inverseSurfaceActive,
  "--ak-v2-inverse-rule": THEME_A_TOKENS.colors.inverseRule,
  "--ak-v2-surface-inverse-accent": THEME_A_TOKENS.colors.surfaceInverseAccent,
  "--ak-v2-focus-inverse": THEME_A_TOKENS.colors.focusInverse,
  "--ak-v2-skeleton-shimmer": THEME_A_TOKENS.colors.skeletonShimmer,
  "--ak-v2-ambiance-terracotta": THEME_A_TOKENS.colors.ambianceTerracotta,
  "--ak-v2-ambiance-sable": THEME_A_TOKENS.colors.ambianceSable,
  "--ak-v2-ambiance-olive": THEME_A_TOKENS.colors.ambianceOlive,
  "--ak-v2-ambiance-sauge": THEME_A_TOKENS.colors.ambianceSauge,
  "--ak-v2-ambiance-ardoise": THEME_A_TOKENS.colors.ambianceArdoise,
  "--ak-v2-ambiance-prune": THEME_A_TOKENS.colors.ambiancePrune,
  "--ak-v2-dataviz-corail": THEME_A_TOKENS.colors.datavizCorail,
  "--ak-v2-dataviz-ambre": THEME_A_TOKENS.colors.datavizAmbre,
  "--ak-v2-dataviz-turquoise": THEME_A_TOKENS.colors.datavizTurquoise,
  "--ak-v2-dataviz-bleu": THEME_A_TOKENS.colors.datavizBleu,
  "--ak-v2-dataviz-violet": THEME_A_TOKENS.colors.datavizViolet,
  "--ak-v2-dataviz-rose": THEME_A_TOKENS.colors.datavizRose,
  "--ak-v2-signal-information": THEME_A_TOKENS.colors.signalInformation,
  "--ak-v2-signal-progress": THEME_A_TOKENS.colors.signalProgress,
  "--ak-v2-signal-warning": THEME_A_TOKENS.colors.signalWarning,
  "--ak-v2-signal-warning-ink": THEME_A_TOKENS.colors.signalWarningInk,
  "--ak-v2-outcome-positive": THEME_A_TOKENS.colors.outcomePositive,
  "--ak-v2-outcome-positive-ink": THEME_A_TOKENS.colors.outcomePositiveInk,
  "--ak-v2-outcome-neutral": THEME_A_TOKENS.colors.outcomeNeutral,
  "--ak-v2-outcome-neutral-ink": THEME_A_TOKENS.colors.outcomeNeutralInk,
  "--ak-v2-outcome-negative": THEME_A_TOKENS.colors.outcomeNegative,
  "--ak-v2-outcome-negative-ink": THEME_A_TOKENS.colors.outcomeNegativeInk,
  "--ak-v2-category-question-1": THEME_A_TOKENS.colors.categoryQuestion1,
  "--ak-v2-category-question-2": THEME_A_TOKENS.colors.categoryQuestion2,
  "--ak-v2-category-question-3": THEME_A_TOKENS.colors.categoryQuestion3,
  "--ak-v2-category-question-4": THEME_A_TOKENS.colors.categoryQuestion4,
  "--ak-v2-category-question-default": THEME_A_TOKENS.colors.categoryQuestionDefault,
  "--ak-v2-category-ethogram-1": THEME_A_TOKENS.colors.categoryEthogram1,
  "--ak-v2-category-ethogram-2": THEME_A_TOKENS.colors.categoryEthogram2,
  "--ak-v2-category-ethogram-3": THEME_A_TOKENS.colors.categoryEthogram3,
  "--ak-v2-category-ethogram-4": THEME_A_TOKENS.colors.categoryEthogram4,
  "--ak-v2-category-ethogram-5": THEME_A_TOKENS.colors.categoryEthogram5,
  "--ak-v2-category-ethogram-6": THEME_A_TOKENS.colors.categoryEthogram6,
  "--ak-v2-category-ethogram-0": THEME_A_TOKENS.colors.categoryEthogram0,
  "--ak-v2-category-ethogram-fallback": THEME_A_TOKENS.colors.categoryEthogramFallback,
  "--ak-v2-control-height": THEME_A_TOKENS.geometry.controlHeight,
  "--ak-v2-radius-control": THEME_A_TOKENS.geometry.radiusControl,
  "--ak-v2-radius-surface": THEME_A_TOKENS.geometry.radiusSurface,
  "--ak-v2-shadow-surface": THEME_A_TOKENS.geometry.shadowSurface,
  "--ak-v2-shadow-overlay": THEME_A_TOKENS.geometry.shadowOverlay,
  "--ak-v2-shadow-raised": THEME_A_TOKENS.geometry.shadowRaised,
  "--ak-v2-shadow-brand-halo": THEME_A_TOKENS.geometry.shadowBrandHalo,
  "--ak-v2-shadow-rail": THEME_A_TOKENS.geometry.shadowRail,
  "--ak-v2-space-1": THEME_A_TOKENS.spacing[1],
  "--ak-v2-space-2": THEME_A_TOKENS.spacing[2],
  "--ak-v2-space-3": THEME_A_TOKENS.spacing[3],
  "--ak-v2-space-4": THEME_A_TOKENS.spacing[4],
  "--ak-v2-space-5": THEME_A_TOKENS.spacing[5],
  "--ak-v2-space-6": THEME_A_TOKENS.spacing[6],
  "--ak-v2-motion-fast": THEME_A_TOKENS.motion.durationFast,
  "--ak-v2-motion-standard": THEME_A_TOKENS.motion.durationStandard,
  "--ak-v2-motion-entrance": THEME_A_TOKENS.motion.durationEntrance,
  "--ak-v2-motion-accent": THEME_A_TOKENS.motion.durationAccent,
  "--ak-v2-motion-easing": THEME_A_TOKENS.motion.easingStandard,
  "--ak-v2-motion-easing-spring": THEME_A_TOKENS.motion.easingSpring,
});
